import React from "react";
import { matchFont } from "@veyso/core";
import type { api } from "../api.js";
import { LogoUploadButton, LookColourFields, colourRoles } from "./LogoAndColours.js";
import { uploadColorsFrom, type SiteLook } from "./wizard.js";

export type SiteBrand = Awaited<ReturnType<typeof api.brandFromWebsite>>;

/**
 * What a film made from a website will be dressed in, before it is made — and
 * a way to change it.
 *
 * The site's own logo, colours and typeface, read the way the server reads
 * them when the film is made — so nobody waits ten minutes to find out the
 * film is in the wrong company's colours. What a site offers as its logo is
 * sometimes a social card or a blurry icon, and its colours sometimes a
 * cookie banner's, so the logo and the colours can each be replaced. For this
 * film only: the brand kit is neither used nor changed.
 */
export const SiteBrandPreview: React.FC<{
  state: { kind: "reading" } | { kind: "read"; brand: SiteBrand } | { kind: "failed"; message: string };
  look: SiteLook;
  onLookChange: (look: SiteLook) => void;
}> = ({ state, look, onLookChange }) => {
  if (state.kind === "reading") {
    return <p className="text-[12px] text-ink-400">Reading the site's logo and colours…</p>;
  }
  if (state.kind === "failed") {
    return <p className="text-[12px] text-signal-warn">{state.message}</p>;
  }

  const { brand } = state;
  const host = new URL(brand.website).hostname.replace(/^www\./, "");
  const siteLogo = brand.logoUrl ?? brand.logoDataUri ?? null;
  const logo = look.logo?.url ?? siteLogo;
  const colors = look.colors ?? brand.colors;
  // What the server will put on the film: a face we bundle, or the look's own.
  const font = brand.typeface?.rendered ? matchFont(brand.typeface.name) : null;

  const reset = "w-fit text-[12px] font-medium text-ember-600 hover:underline";

  return (
    <div className="flex flex-col gap-4 rounded-[10px] border border-[var(--edge)] bg-bay-900 p-4">
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-[12.5px] font-semibold text-ink-100">Logo</h3>
          <span className="text-[11px] text-ink-400">
            {look.logo ? "Yours, for this video" : siteLogo ? `From ${host}` : "None found"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div
            className="flex h-16 w-32 shrink-0 items-center justify-center overflow-hidden rounded-[8px] border border-[var(--edge)] px-3"
            style={{ background: colors.background }}
          >
            {logo ? (
              <img src={logo} alt={`${brand.name ?? host} logo`} className="max-h-12 max-w-full object-contain" />
            ) : (
              <span className="truncate text-[14px] font-semibold" style={{ color: colors.text }}>
                {brand.name ?? host}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <LogoUploadButton
              hasLogo={logo !== null}
              onUploaded={(stored) => onLookChange({ ...look, logo: { key: stored.logoKey, url: stored.logoUrl } })}
            />
            {look.logo ? (
              <button type="button" className={reset} onClick={() => onLookChange({ ...look, logo: null })}>
                Use the site's logo
              </button>
            ) : null}
          </div>
        </div>
        {!logo ? <p className="text-[11px] text-ink-400">Without one, the film sets the name as a wordmark.</p> : null}
      </div>

      <div className="flex flex-col gap-2 border-t border-[var(--edge)] pt-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="text-[12.5px] font-semibold text-ink-100">Colours</h3>
          <span className="text-[11px] text-ink-400">{look.colors ? "Yours, for this video" : `From ${host}`}</span>
        </div>
        {look.colors ? (
          <>
            <LookColourFields colors={look.colors} onChange={(next) => onLookChange({ ...look, colors: next })} />
            <button type="button" className={reset} onClick={() => onLookChange({ ...look, colors: null })}>
              Use the site's colours
            </button>
          </>
        ) : (
          <>
            <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {colourRoles.map((role) => (
                <li key={role.key} className="flex items-center gap-2.5">
                  <span
                    aria-hidden
                    className="h-7 w-7 shrink-0 rounded-full border border-[var(--edge)]"
                    style={{ background: brand.colors[role.key] }}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-[12px] text-ink-100">
                      {role.label} <span className="tnum uppercase text-ink-400">{brand.colors[role.key]}</span>
                    </span>
                    <span className="text-[10.5px] text-ink-400">{role.use}</span>
                  </span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="btn btn-secondary w-fit"
              // Seeded as #rrggbb, which is all the start request takes.
              onClick={() => onLookChange({ ...look, colors: uploadColorsFrom(brand.colors) })}
            >
              Change colours
            </button>
          </>
        )}
      </div>

      {font ? (
        <p className="text-[12px] text-ink-300" style={{ fontFamily: `"${font}", sans-serif` }}>
          {font} · used for headlines and text
        </p>
      ) : brand.typeface ? (
        <p className="text-[12px] text-ink-400">
          {brand.typeface.name} isn't available, so the style's fonts are used.
        </p>
      ) : null}

      <p className="text-[11.5px] leading-[1.5] text-ink-400">
        Read from {host}. Anything you change here is for this video only; your brand kit is not changed.
      </p>
    </div>
  );
};
